import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Play,
  ArrowRight,
  Clock,
  Calendar
} from 'lucide-react';
import { testimonials } from '../data/mock';

const Home = () => {
  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Hero Section - Completely Redesigned */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gold/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Side */}
            <div>
              {/* Eyebrow Text */}
              <p className="text-gold text-xs font-medium uppercase tracking-[0.3em] mb-8">
                Season 3 · 2026 · Pure Sciences
              </p>
              
              {/* Main Headline */}
              <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-medium text-warm-white mb-4 leading-tight">
                The question they always asked.
              </h1>
              
              {/* Hindi Subheadline */}
              <p className="font-playfair text-3xl md:text-4xl lg:text-5xl italic text-gold mb-8">
                BSc se kya hoga?
              </p>
              
              {/* Description */}
              <p className="text-lg text-warm-white/60 mb-10 leading-relaxed max-w-xl">
                India's first podcast series dedicated entirely to pure science careers. Every Monday — one expert, one real career, one roadmap a student can follow starting today.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://open.spotify.com/show/4Imx8vJprGKh7dqYvOx3KZ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gold hover:bg-gold/90 text-dark-bg font-semibold text-sm uppercase tracking-wider px-8 py-4 inline-flex items-center justify-center transition-all duration-300"
                >
                  <Play className="mr-2 w-4 h-4" fill="currentColor" />
                  Listen Now
                </a>
                <Link to="/episodes">
                  <Button
                    variant="outline"
                    className="text-sm uppercase tracking-wider px-8 py-4 border-gold text-gold hover:bg-gold hover:text-dark-bg bg-transparent transition-all duration-300 rounded-none"
                  >
                    All 70+ Episodes
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Side - Latest Episode Card */}
            <div className="relative">
              <Card className="relative overflow-hidden border border-dark-border bg-dark-card hover:border-gold/50 transition-all duration-300 group">
                {/* Episode Image/Header */}
                <div 
                  className="h-64 bg-cover bg-center relative"
                  style={{ 
                    backgroundImage: `url(https://img.youtube.com/vi/wOmCgyMhICE/maxresdefault.jpg)`,
                    backgroundPosition: 'center top'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-bg via-dark-bg/60 to-transparent"></div>
                  
                  {/* Episode Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="text-gold text-xs font-medium uppercase tracking-widest bg-dark-bg/80 backdrop-blur-sm px-3 py-1.5 border border-gold/30">
                      S3 · Episode 2 · Physics
                    </span>
                  </div>
                  
                  {/* Play Button */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-10 h-10 bg-gold flex items-center justify-center">
                      <Play className="w-4 h-4 text-dark-bg ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                </div>
                
                <CardContent className="p-6 bg-dark-card">
                  {/* Episode Title */}
                  <h3 className="font-playfair text-2xl font-medium text-warm-white mb-3 group-hover:text-gold transition-colors leading-tight">
                    He Got a BITS Seat. He Chose BSc Physics Instead.
                  </h3>
                  
                  {/* Guest Info */}
                  <div className="mb-4">
                    <p className="text-warm-white font-medium">Dr. Sai Santosh Kumar Raavi</p>
                    <p className="text-warm-white/50 text-sm">Professor of Physics, IIT Hyderabad · FRSC</p>
                  </div>
                  
                  {/* Meta Info */}
                  <div className="flex items-center gap-4 text-xs text-warm-white/50 uppercase tracking-wider mb-5">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Feb 2026
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      45 min
                    </span>
                  </div>
                  
                  {/* Play Button */}
                  <a
                    href="https://youtu.be/wOmCgyMhICE"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-play-episode-link"
                    className="w-full bg-gold hover:bg-gold/90 text-dark-bg font-semibold py-3 px-6 text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Play className="w-3 h-3" fill="currentColor" />
                    Play Episode
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-dark-card border-y border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="font-playfair text-4xl md:text-5xl font-medium text-gold mb-2">
                3
              </div>
              <div className="text-warm-white/50 text-[10px] uppercase tracking-[0.2em]">Seasons</div>
            </div>
            <div className="text-center">
              <div className="font-playfair text-4xl md:text-5xl font-medium text-gold mb-2">
                70+
              </div>
              <div className="text-warm-white/50 text-[10px] uppercase tracking-[0.2em]">Total Episodes</div>
            </div>
            <div className="text-center">
              <div className="font-playfair text-4xl md:text-5xl font-medium text-gold mb-2">
                65+
              </div>
              <div className="text-warm-white/50 text-[10px] uppercase tracking-[0.2em]">Guests Featured</div>
            </div>
            <div className="text-center">
              <div className="font-playfair text-4xl md:text-5xl font-medium text-gold mb-2">
                5
              </div>
              <div className="text-warm-white/50 text-[10px] uppercase tracking-[0.2em]">Themes Explored</div>
            </div>
            <div className="text-center col-span-2 md:col-span-1">
              <div className="font-playfair text-4xl md:text-5xl font-medium text-gold mb-2">
                45
              </div>
              <div className="text-warm-white/50 text-[10px] uppercase tracking-[0.2em]">Min Per Episode</div>
            </div>
          </div>
        </div>
      </section>

      {/* Season 3 — Science Unlocked Section */}
      <section className="py-20 bg-dark-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between mb-16">
            <div>
              <p className="text-gold text-xs font-medium uppercase tracking-[0.3em] mb-4">
                Now Airing · Season 3
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl font-medium text-warm-white">
                Science Unlocked
              </h2>
            </div>
            <Link to="/episodes" className="hidden md:flex items-center gap-2 text-warm-white/60 hover:text-gold transition-colors text-sm group">
              View all 16 episodes
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Card 1 - Anil Kumar Kruthiventi */}
            <a
              href="https://www.youtube.com/watch?v=P-j1kyDOxMc"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="season3-strip-ep1-link"
              className="bg-[#111] border border-dark-border hover:border-gold/40 transition-all duration-300 group cursor-pointer p-6 flex flex-col min-h-[240px]"
            >
              <p className="text-gold text-xs font-medium uppercase tracking-[0.2em] mb-6">
                S3 · EP 01 · Chemistry
              </p>
              <div className="flex-1">
                <h3 className="font-playfair text-xl md:text-2xl font-medium text-warm-white mb-2 group-hover:text-gold transition-colors">
                  Anil Kumar Kruthiventi
                </h3>
                <p className="text-warm-white/50 text-sm leading-relaxed">
                  BSc Chem → CSO, Shapoorji Pallonji affiliate
                </p>
              </div>
              <div className="flex items-center justify-between mt-6">
                <span className="text-[10px] text-gold uppercase tracking-widest bg-gold/10 px-3 py-1 rounded-full">
                  Pharma · Agri
                </span>
                <ArrowRight className="w-5 h-5 text-warm-white/30 group-hover:text-gold transition-colors" />
              </div>
            </a>

            {/* Card 2 - Dr. Sai Santosh Kumar Raavi */}
            <div className="bg-[#111] border border-dark-border hover:border-gold/40 transition-all duration-300 group cursor-pointer p-6 flex flex-col min-h-[240px]">
              <p className="text-gold text-xs font-medium uppercase tracking-[0.2em] mb-6">
                S3 · EP 02 · Physics
              </p>
              <div className="flex-1">
                <h3 className="font-playfair text-xl md:text-2xl font-medium text-warm-white mb-2 group-hover:text-gold transition-colors">
                  Dr. Sai Santosh Kumar Raavi
                </h3>
                <p className="text-warm-white/50 text-sm leading-relaxed">
                  SSSIHL Physics → Professor, IIT Hyderabad · FRSC
                </p>
              </div>
              <div className="flex items-center justify-between mt-6">
                <span className="text-[10px] text-gold uppercase tracking-widest bg-gold/10 px-3 py-1 rounded-full">
                  Quantum · Photonics
                </span>
                <ArrowRight className="w-5 h-5 text-warm-white/30 group-hover:text-gold transition-colors" />
              </div>
            </div>

            {/* Card 3 - Prof. Rajesh Gopakumar */}
            <div className="bg-[#111] border border-dark-border hover:border-gold/40 transition-all duration-300 group cursor-pointer p-6 flex flex-col min-h-[240px]">
              <p className="text-gold text-xs font-medium uppercase tracking-[0.2em] mb-6">
                S3 · EP 03 · Physics
              </p>
              <div className="flex-1">
                <h3 className="font-playfair text-xl md:text-2xl font-medium text-warm-white mb-2 group-hover:text-gold transition-colors">
                  Prof. Rajesh Gopakumar
                </h3>
                <p className="text-warm-white/50 text-sm leading-relaxed">
                  JEE AIR 1, 1987 → Director, ICTS-TIFR Bengaluru
                </p>
              </div>
              <div className="flex items-center justify-between mt-6">
                <span className="text-[10px] text-gold uppercase tracking-widest bg-gold/10 px-3 py-1 rounded-full">
                  String Theory
                </span>
                <ArrowRight className="w-5 h-5 text-warm-white/30 group-hover:text-gold transition-colors" />
              </div>
            </div>
          </div>

          <div className="mt-10 text-center md:hidden">
            <Link to="/episodes" className="inline-flex items-center gap-2 text-warm-white/60 hover:text-gold transition-colors text-sm">
              View all 16 episodes
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Season Archive Section */}
      <section className="py-20 bg-dark-card border-y border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <p className="text-gold text-xs font-medium uppercase tracking-[0.3em] mb-4">
              The Full Archive
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl font-medium text-warm-white">
              Three seasons of learning
            </h2>
          </div>

          {/* Season 3 Row */}
          <div className="flex flex-col md:flex-row gap-8 py-10 border-b border-dark-border/50">
            <div className="md:w-32 flex-shrink-0">
              <span className="font-playfair text-6xl md:text-7xl font-medium text-gold/20">S3</span>
            </div>
            <div className="flex-1">
              <p className="text-warm-white/60 text-sm mb-4">
                Science Unlocked · 2026 · 16 episodes planned
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {['Pure Sciences', 'BSc Careers', 'Chemistry', 'Physics', 'Mathematics', 'Bioscience'].map((tag) => (
                  <span key={tag} className="text-[10px] text-warm-white/50 uppercase tracking-wider border border-dark-border px-3 py-1.5 hover:border-gold/50 hover:text-gold transition-colors cursor-default">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {['Anil Kumar Kruthiventi', 'Dr. Sai Santosh Kumar Raavi', 'Prof. Rajesh Gopakumar'].map((guest) => (
                  <span key={guest} className="text-xs text-warm-white/70 border border-dark-border px-3 py-1.5 hover:border-gold/50 hover:text-gold transition-colors cursor-default">
                    {guest}
                  </span>
                ))}
                <span className="text-xs text-warm-white/30 border border-dark-border/50 px-3 py-1.5">
                  + 13 coming soon
                </span>
              </div>
            </div>
          </div>

          {/* Season 2 Row */}
          <div className="flex flex-col md:flex-row gap-8 py-10 border-b border-dark-border/50">
            <div className="md:w-32 flex-shrink-0">
              <span className="font-playfair text-6xl md:text-7xl font-medium text-warm-white/10">S2</span>
            </div>
            <div className="flex-1">
              <p className="text-warm-white/60 text-sm mb-4">
                Season 2 · 2024–25 · 48 episodes
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {['Leadership & Influence', 'Technology & Innovation', 'Life Awareness & Human Growth', 'Career Growth & Reinvention', 'Future of Work & Learning'].map((tag) => (
                  <span key={tag} className="text-[10px] text-warm-white/50 uppercase tracking-wider border border-dark-border px-3 py-1.5 hover:border-gold/50 hover:text-gold transition-colors cursor-default">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {['Abhijit Bhattacharya', 'Andrew Wallbridge', 'Bhavna Toor', 'Deepak Mohan', 'Pankaj Rai', 'Shubhayu Sengupta', 'Swetha Reddy', 'Zach Cherian'].map((guest) => (
                  <span key={guest} className="text-xs text-warm-white/70 border border-dark-border px-3 py-1.5 hover:border-gold/50 hover:text-gold transition-colors cursor-default">
                    {guest}
                  </span>
                ))}
                <span className="text-xs text-warm-white/30 border border-dark-border/50 px-3 py-1.5">
                  + 30 more guests
                </span>
              </div>
            </div>
          </div>

          {/* Season 1 Row */}
          <div className="flex flex-col md:flex-row gap-8 py-10">
            <div className="md:w-32 flex-shrink-0">
              <span className="font-playfair text-6xl md:text-7xl font-medium text-warm-white/10">S1</span>
            </div>
            <div className="flex-1">
              <p className="text-warm-white/60 text-sm mb-4">
                Season 1 · 2023–24 · 17 episodes
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {['Leadership & Influence', 'Technology & Innovation', 'Life Awareness & Human Growth', 'Career Growth & Reinvention', 'Future of Work & Learning'].map((tag) => (
                  <span key={tag} className="text-[10px] text-warm-white/50 uppercase tracking-wider border border-dark-border px-3 py-1.5 hover:border-gold/50 hover:text-gold transition-colors cursor-default">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {['Praveen Pantula', 'Suresh Neelakantan', 'Pramod Chandrasekhar', 'Nigel Hughes', 'Devika Rani Karri', 'Amit Anand', 'Dr. Lalith Sanagavarapu', 'Gagandeep Taluja', 'Sachin Ghorpade IPS'].map((guest) => (
                  <span key={guest} className="text-xs text-warm-white/70 border border-dark-border px-3 py-1.5 hover:border-gold/50 hover:text-gold transition-colors cursor-default">
                    {guest}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Be a Guest CTA Section */}
      <section className="py-28 md:py-36 relative overflow-hidden">
        {/* Warm gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-bg via-[#0d0b08] to-[#0f0c06]"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gold/[0.03] to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-gold/[0.02] to-transparent"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gold text-xs font-medium uppercase tracking-[0.3em] mb-6">
            Join the Show
          </p>
          
          <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-medium text-warm-white mb-8 leading-tight">
            Your story belongs on<br />Science Unlocked
          </h2>
          
          <p className="text-lg md:text-xl text-warm-white/60 mb-12 max-w-2xl mx-auto leading-relaxed">
            If you are a scientist, researcher, or industry leader who started with a pure science degree — one conversation with us can change how a 17-year-old sees their future.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-gold hover:bg-gold/90 text-dark-bg font-semibold text-sm uppercase tracking-wider px-10 py-4 transition-all duration-300 w-full sm:w-auto">
                Apply to Be a Guest
              </button>
            </Link>
            <Link to="/about">
              <button className="border border-gold text-gold hover:bg-gold hover:text-dark-bg font-semibold text-sm uppercase tracking-wider px-10 py-4 transition-all duration-300 w-full sm:w-auto">
                Learn About the Show
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-dark-card border-y border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl md:text-4xl font-medium text-warm-white mb-4">
              What Listeners Are Saying
            </h2>
            <p className="text-warm-white/60 text-sm max-w-2xl mx-auto">
              Join a community of learners who start their week with intention
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <Card
                key={testimonial.id}
                className="p-8 border border-dark-border bg-dark-bg hover:border-gold/30 transition-all duration-300"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gold flex items-center justify-center text-dark-bg font-bold text-lg flex-shrink-0">
                    {testimonial.avatar}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-warm-white">
                      {testimonial.name}
                    </div>
                    <div className="text-xs text-warm-white/50 uppercase tracking-wider">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
                <p className="text-warm-white/70 leading-relaxed mb-4 italic font-playfair text-lg">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-1.5 h-1.5 bg-gold rounded-full"></div>
                  <span className="text-gold uppercase tracking-wider">
                    {testimonial.outcome}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-bg relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gold/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-playfair text-3xl md:text-5xl font-medium text-warm-white mb-6">
            Get Weekly Insights That Actually Work
          </h2>
          <p className="text-lg text-warm-white/60 mb-4 max-w-2xl mx-auto">
            Every Monday morning, receive practical strategies, episode highlights, and science-backed takeaways you can apply immediately.
          </p>
          <p className="text-sm text-warm-white/50 mb-10 max-w-xl mx-auto">
            No fluff. No spam. Just actionable insights to help you learn better, lead stronger, and live more intentionally.
          </p>
          <Link to="/newsletter">
            <Button className="bg-gold hover:bg-gold/90 text-dark-bg font-semibold text-sm uppercase tracking-wider px-10 py-4 transition-all duration-300 rounded-none">
              Get Your Weekly Edge — Free
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
